/* eslint-disable prettier/prettier */
import React, { useEffect, useState } from 'react';

interface Point {
  x: number;
  y: number;
}

interface LogAnnotation {
  cx: number;
  cy: number;
  r: number;
}

interface AnnotationData {
  annotations?: {
    logs?: LogAnnotation[];
    stack_area?: Point[];
    reference_area?: Point[];
    stack_heights?: [Point, Point][];
    stack_length?: Point[];
  };
}

const useAnnotationStyles = () => ({
  strokeWidth: 5,
  xSize: 30,
  colors: {
    logs: 'green',
    stackArea: 'blue',
    referenceArea: 'cyan',
    xMark: 'red',
  },
});

type AnnotatedImageProps = {
  src: string;
  annotation?: string;
  className?: string;
};

function AnnotatedImage({
  src,
  annotation,
  className,
}: AnnotatedImageProps): React.ReactElement {
  const styles = useAnnotationStyles();
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);

  const [annotationJ, setAnnotationJ] = useState<AnnotationData | undefined>(
    undefined,
  );

  useEffect(() => {
    // get image dimensions for svg viewBox
    const img = new Image();

    img.onload = function () {
      setHeight(img.height);
      setWidth(img.width);
    };

    img.src = src;
  }, [src]);

  useEffect(() => {
    // decode and parse annotation
    setAnnotationJ(
      annotation ? JSON.parse(atob(annotation.split(',')[1])) : undefined,
    );
  }, [annotation]);

  // Responsive X mark rendering
  const renderXMark = (x: number, y: number, size: number) => {
    const halfSize = size / 2;
    const strokeW = size / 6;
    return (
      <g transform={`translate(${x}, ${y})`}>
        <line
          x1={-halfSize}
          y1={-halfSize}
          x2={halfSize}
          y2={halfSize}
          stroke={styles.colors.xMark}
          strokeWidth={strokeW}
          strokeLinecap="round"
        />
        <line
          x1={halfSize}
          y1={-halfSize}
          x2={-halfSize}
          y2={halfSize}
          stroke={styles.colors.xMark}
          strokeWidth={strokeW}
          strokeLinecap="round"
        />
      </g>
    );
  };

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className={className}>
      <image href={src} />
      {annotationJ?.annotations?.logs?.map((log, i) => (
        <circle
          key={i}
          cx={log.cx}
          cy={log.cy}
          r={log.r}
          strokeWidth={styles.strokeWidth}
          stroke={styles.colors.logs}
          fill="none"
        />
      ))}

      {annotationJ?.annotations?.stack_area && (
        <polygon
          points={annotationJ.annotations.stack_area
            .map((point) => `${point.x},${point.y}`)
            .join(' ')}
          strokeWidth={styles.strokeWidth}
          stroke={styles.colors.stackArea}
          fill="none"
        />
      )}

      {annotationJ?.annotations?.reference_area && (
        <polygon
          points={annotationJ.annotations.reference_area
            .map((point) => `${point.x},${point.y}`)
            .join(' ')}
          strokeWidth={styles.strokeWidth}
          stroke={styles.colors.referenceArea}
          fill="none"
        />
      )}

      {annotationJ?.annotations?.stack_heights &&
        annotationJ.annotations.stack_heights.map((stackHeight, i) => (
          <React.Fragment key={i}>
            {renderXMark(stackHeight[0].x, stackHeight[0].y, styles.xSize)}
            {renderXMark(stackHeight[1].x, stackHeight[1].y, styles.xSize)}
          </React.Fragment>
        ))}

      {/* {annotationJ?.annotations?.stack_length && (
                <polyline
                    points={annotationJ.annotations.stack_length
                        .map((point: any) => `${point.x},${point.y}`)
                        .join(' ')}
                    strokeWidth={styles.strokeWidth}
                    stroke={styles.colors.referenceArea}
                    fill="none"
                />
            )} */}
    </svg>
  );
}

export default AnnotatedImage;
